import { useState, useEffect, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import {
  X,
  Save,
  Calendar as CalendarIcon,
  Edit,
  Loader2,
  Building2,
  CreditCard,
  ArrowLeft,
  CheckCircle,
  AlertCircle,
} from "lucide-react";
import { format, isSameDay, isBefore, startOfDay } from "date-fns";
import { validateSouthAfricanId } from "@/utils/saIdValidation";
import {
  useAvailableSlots,
  useAvailableDates,
  useCreateBooking,
} from "@/hooks/useBookings";
import { useAuth } from "@/hooks/useAuth";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import apiClient from "@/api/apiClient";
import { getDoctorName } from "@/utils/dataLookups";
import { showSuccess, showError, showWarning } from "@/utils/toastHelpers";
import { getErrorMessage } from "@/utils/errorUtils";
import { transformBookingToBackend } from "@/services/transformers";
import PaymentForm from "@/components/payfast/PaymentForm";

// Import shared components
import TimeSlotSelection from "./TimeSlotSelection";
import DoctorSelection from "./DoctorSelection";
import PatientInfoForm from "./PatientInfoForm";
import ServiceInfoDisplay from "./ServiceInfoDisplay";

export default function PatientAppointmentForm({
  appointmentToEdit,
  serviceForBooking,
  services,
  onSave,
  onClose,
}) {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const [formData, setFormData] = useState({
    patient_id: "",
    patient_database_id: "",
    patient_email: "",
    service_id: "",
    practice_id: "",
    date: "",
    start_time: "",
    end_time: "",
    doctor_id: "",
    room_id: "",
    status: "scheduled",
    notes: "",
    total_price: 0,
  });

  const [selectedService, setSelectedService] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [doctorFilter, setDoctorFilter] = useState(null);
  const [idValidation, setIdValidation] = useState(null);
  const [idTouched, setIdTouched] = useState(false);

  // Payment state
  const [step, setStep] = useState("details"); // "details" | "payment" | "success"
  const [paymentUuid, setPaymentUuid] = useState(null);
  const [paymentId, setPaymentId] = useState(null);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [hideForPayfast, setHideForPayfast] = useState(false);

  const dateStr = selectedDate ? format(selectedDate, "yyyy-MM-dd") : null;

  // Check if service requires payment
  const serviceRequiresPayment =
    selectedService?.base_price && selectedService.base_price > 0;

  // Check if doctor can receive payments (PayFast merchant status)
  const { data: doctorPaymentStatus } = useQuery({
    queryKey: ["doctor-payment-status", formData.doctor_id],
    queryFn: () =>
      apiClient.get(`/api/payfast/doctor/${formData.doctor_id}/status`),
    enabled: !!formData.doctor_id && !!serviceRequiresPayment,
  });

  const doctorCanReceivePayments = doctorPaymentStatus?.canReceivePayments;

  // Get available dates and comprehensive availability data
  const { data: availabilityData, isLoading: loadingAvailability } = useAvailableDates(
    selectedService?.id,
    null,
    60
  );


  // Fetch doctors data for name lookups
  const practiceIdForDoctors = serviceForBooking?.selectedPracticeId ||
                               serviceForBooking?.practice_id ||
                               user?.practiceId;

  const { data: doctors = [] } = useQuery({
    queryKey: ["doctors", practiceIdForDoctors, selectedService?.id],
    queryFn: () => {
      // If we have a service ID, fetch doctors for that service
      if (selectedService?.id) {
        return apiClient.get(`/api/users/doctors?serviceId=${selectedService.id}`);
      }

      // For patients, pass practiceId as query param
      if (user?.userType === "patient" && practiceIdForDoctors) {
        return apiClient.get(`/api/users/doctors?practiceId=${practiceIdForDoctors}`);
      }
      // For doctors/practice owners, use standard endpoint
      return apiClient.get("/api/users/doctors");
    },
    enabled: !!selectedService?.id || !!practiceIdForDoctors || user?.userType !== "patient",
    staleTime: 5 * 60 * 1000,
  });

  // Convert dates to Set for faster lookup
  const availableDates = useMemo(() => {
    if (Array.isArray(availabilityData)) {
      // When no dates available, availabilityData is an empty array
      return new Set([]);
    }
    // When dates are available, availabilityData has a dates property
    return new Set(availabilityData?.dates || []);
  }, [availabilityData]);

  // Get slots from cached availability data when possible
  const slotsForSelectedDate = useMemo(() => {
    if (!selectedDate || !availabilityData?.availability) return [];

    const dateEntry = availabilityData.availability.find(
      (item) => item.date === dateStr
    );
    return dateEntry?.slots || [];
  }, [availabilityData?.availability, dateStr, selectedDate]);

  // Only make API call if we don't have cached slots
  const hasCachedSlots = slotsForSelectedDate.length > 0;
  const { data: apiSlotsData, isLoading: loadingSlots } = useAvailableSlots(
    selectedService?.id,
    hasCachedSlots ? null : dateStr
  );

  // Use cached slots when available, otherwise use API data
  const allSlots = useMemo(() => {
    return slotsForSelectedDate.length > 0
      ? slotsForSelectedDate
      : apiSlotsData || [];
  }, [slotsForSelectedDate, apiSlotsData]);

  const actuallyLoadingSlots = !hasCachedSlots && loadingSlots;

  const createBookingMutation = useCreateBooking();
  const isEditing = !!appointmentToEdit;

  // Create PayFast payment session mutation
  const createPaymentMutation = useMutation({
    mutationFn: (data) =>
      apiClient.post("/api/payfast/payments/create-session", data),
    onSuccess: (data) => {
      setPaymentUuid(data.uuid);
      setPaymentId(data.paymentId);
      setStep("payment");
    },
    onError: (error) => {
      showError("Failed to initialize payment", getErrorMessage(error));
    },
  });

  // Confirm payment and create booking mutation
  const confirmPaymentMutation = useMutation({
    mutationFn: (data) =>
      apiClient.post(
        `/api/payfast/payments/${paymentId}/confirm-and-book`,
        data
      ),
    onSuccess: (data) => {
      setStep("success");
      queryClient.invalidateQueries({ queryKey: ["bookings"] });
      queryClient.invalidateQueries({ queryKey: ["calendar"] });
      queryClient.invalidateQueries({ queryKey: ["available-doctors"] });
      queryClient.invalidateQueries({ queryKey: ["available-rooms"] });
    },
    onError: (error) => {
      showError("Failed to complete booking", getErrorMessage(error));
      setIsProcessingPayment(false);
    },
  });

  // Filter slots based on selected doctor
  const availableSlots = useMemo(() => {
    if (!allSlots.length) return [];

    // When editing, show all slots if no doctor is selected yet
    if (selectedService?.requires_doctor && !doctorFilter && !isEditing)
      return [];

    return allSlots.filter((slot) =>
      selectedService?.requires_doctor && doctorFilter
        ? slot.doctorId === doctorFilter
        : true
    );
  }, [allSlots, doctorFilter, selectedService, isEditing]);

  // Initialize form data
  useEffect(() => {
    if (isEditing) {
      setFormData({
        id: appointmentToEdit.id,
        patient_id: appointmentToEdit.patient_id || "",
        patient_database_id: appointmentToEdit.patient_database_id || "",
        patient_email: appointmentToEdit.patient_email || "",
        service_id: appointmentToEdit.service_id || "",
        date: appointmentToEdit.date || "",
        start_time: appointmentToEdit.start_time || "",
        end_time: appointmentToEdit.end_time || "",
        doctor_id: appointmentToEdit.doctor_id || "",
        room_id: appointmentToEdit.room_id || "",
        status: appointmentToEdit.status || "scheduled",
        notes: appointmentToEdit.notes || "",
        total_price: appointmentToEdit.total_price || 0,
      });

      if (appointmentToEdit.date) {
        setSelectedDate(new Date(appointmentToEdit.date));
      }

      if (appointmentToEdit.doctor_id) {
        setDoctorFilter(appointmentToEdit.doctor_id);
      }

      const service = services.find(
        (s) => s.id === appointmentToEdit.service_id
      );
      setSelectedService(service);
    } else if (serviceForBooking) {
      const service =
        services.find((s) => s.id === serviceForBooking.id) ||
        serviceForBooking;
      setSelectedService(service);
      setFormData((prev) => ({
        ...prev,
        service_id: serviceForBooking.id,
        practice_id:
          serviceForBooking.selectedPracticeId ||
          serviceForBooking.practice_id ||
          user?.practiceId ||
          "",
        total_price: service?.base_price || 0,
        // Prepopulate patient data since this is for patient booking
        patient_id: user?.identificationNumber || "",
        patient_database_id: user?.id || "",
        patient_email: user?.email || "",
      }));

      // Validate prepopulated ID
      if (user?.identificationNumber) {
        const validation = validateSouthAfricanId(user.identificationNumber);
        setIdValidation(validation);
      }
    }
  }, [appointmentToEdit, serviceForBooking, isEditing, services, user]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));

    if (field === "patient_id") {
      if (value.trim()) {
        const validation = validateSouthAfricanId(value);
        setIdValidation(validation);
      } else {
        setIdValidation(null);
      }
    }
  };

  const handleDateSelect = (date) => {
    if (!date) {
      setSelectedDate(null);
      setFormData((prev) => ({
        ...prev,
        date: "",
        start_time: "",
        end_time: "",
        doctor_id: "",
      }));
      setDoctorFilter(null);
      return;
    }

    if (!(date instanceof Date) || isNaN(date.getTime())) {
      console.warn("Invalid date selected:", date);
      return;
    }

    setSelectedDate(date);
    const dateStr = format(date, "yyyy-MM-dd");
    setFormData((prev) => ({
      ...prev,
      date: dateStr,
      start_time: "",
      end_time: "",
      doctor_id: "",
    }));

    setDoctorFilter(null);
  };

  const handleTimeSlotSelect = (slot) => {
    setFormData((prev) => ({
      ...prev,
      start_time: slot.startTime,
      end_time: slot.endTime,
      doctor_id: doctorFilter || slot.doctorId || "",
      room_id: slot.roomId || "",
    }));
  };

  const isDateAvailable = (date) => {
    const dateStr = format(date, "yyyy-MM-dd");
    return availableDates.has(dateStr);
  };

  const isDateDisabled = (date) => {
    if (isBefore(date, startOfDay(new Date()))) return true;
    if (isEditing && selectedDate && isSameDay(date, selectedDate))
      return false;
    return !isDateAvailable(date);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (
      !formData.patient_id ||
      !formData.patient_email ||
      !formData.date ||
      !formData.start_time
    ) {
      showWarning("Please fill in all required fields");
      return;
    }

    if (idValidation && !idValidation.isValid) {
      showError("Invalid ID number", "Please enter a valid South African ID number");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.patient_email)) {
      showError("Invalid email", "Please enter a valid email address");
      return;
    }

    // Check if payment is required (not editing, service has a price, and doctor selected)
    if (!isEditing && serviceRequiresPayment && formData.doctor_id) {
      if (!doctorCanReceivePayments) {
        showWarning(
          "This doctor is not set up to receive payments. Please select a different doctor or contact the practice."
        );
        return;
      }

      // Create payment session and proceed to payment step
      createPaymentMutation.mutate({
        serviceId: formData.service_id,
        doctorId: formData.doctor_id,
        patientId: formData.patient_database_id,
        patientEmail: formData.patient_email,
        patientIdentificationNumber: formData.patient_id,
      });
      return;
    }

    // No payment required - create booking directly
    try {
      if (isEditing) {
        await onSave(formData);
        onClose();
        showSuccess("Appointment updated successfully");
      } else {
        await createBookingMutation.mutateAsync(formData);
        onClose();
        showSuccess("Appointment created successfully");
      }
    } catch (error) {
      console.error("Error saving appointment:", error);
      showError("Failed to save appointment", error.message || "Please try again");
    }
  };

  // Handle payment success - confirm payment and create booking
  const handlePaymentSuccess = () => {
    setIsProcessingPayment(true);
    const backendData = transformBookingToBackend(formData);
    confirmPaymentMutation.mutate({
      date: backendData.date,
      startTime: backendData.startTime,
      endTime: backendData.endTime,
      roomId: backendData.roomId || undefined,
    });
  };

  // Handle payment error
  const handlePaymentError = (error) => {
    showError("Payment failed", error);
    setIsProcessingPayment(false);
  };

  // Go back to details step
  const handleBackToDetails = () => {
    setStep("details");
    setPaymentUuid(null);
    setPaymentId(null);
  };

  return (
    <>
      <div data-payfast-parent-modal className={`fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4 ${hideForPayfast ? "invisible" : ""}`}>
      <Card className="w-full max-w-5xl bg-white/90 backdrop-blur-xl border-slate-200/60 shadow-2xl max-h-[90vh] overflow-y-auto">
        <CardHeader className="flex flex-row items-center justify-between sticky top-0 bg-white/80 backdrop-blur-sm z-10 border-b">
          <CardTitle className="flex items-center gap-2">
            {isEditing ? (
              <Edit className="w-5 h-5" />
            ) : (
              <CalendarIcon className="w-5 h-5" />
            )}
            {isEditing ? "Edit Appointment" : "Schedule Appointment"}
          </CardTitle>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="w-4 h-4" />
          </Button>
        </CardHeader>
        <CardContent className="p-6">
          {/* Success Step */}
          {step === "success" && (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Booking Confirmed!
              </h3>
              <p className="text-sm text-gray-600 mb-6">
                Your appointment has been booked and payment processed
                successfully.
              </p>
              <Button onClick={onClose}>
                Close
              </Button>
            </div>
          )}

          {/* Payment Step */}
          {step === "payment" && paymentUuid && (
            <div className="max-w-md mx-auto space-y-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleBackToDetails}
                className="mb-2"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to details
              </Button>

              <div className="bg-blue-50 p-3 rounded-lg space-y-2">
                <div className="flex items-center gap-2">
                  <CalendarIcon className="w-4 h-4 text-blue-600" />
                  <span className="text-sm font-medium text-blue-900">
                    {selectedDate && format(selectedDate, "EEEE, MMMM d, yyyy")}
                  </span>
                </div>
                <div className="text-sm text-blue-800">
                  {formData.start_time && format(new Date(`2024-01-01T${formData.start_time}`), "h:mm a")}
                  {formData.end_time && (
                    <> - {format(new Date(`2024-01-01T${formData.end_time}`), "h:mm a")}</>
                  )}
                </div>
                <div className="text-sm text-blue-800">
                  {selectedService?.name}
                </div>
              </div>

              <PaymentForm
                uuid={paymentUuid}
                amount={Math.round(selectedService?.base_price * 100)}
                currency="ZAR"
                onSuccess={handlePaymentSuccess}
                onError={handlePaymentError}
                isProcessing={isProcessingPayment}
                setIsProcessing={setIsProcessingPayment}
                onPayfastReady={() => setHideForPayfast(true)}
                onModalClose={() => setHideForPayfast(false)}
              />
            </div>
          )}

          {/* Details Step */}
          {step === "details" && (
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className={`grid gap-8 ${
              availableDates.size > 0
                ? "lg:grid-cols-3"
                : "lg:grid-cols-2"
            }`}>
              {/* Left Column: Service and Patient Info */}
              <div className="space-y-6">
                <ServiceInfoDisplay selectedService={selectedService} />

                {/* Practice Information */}
                {serviceForBooking?.selectedPracticeName && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 text-blue-800">
                      <Building2 className="w-4 h-4" />
                      <span className="font-medium">Practice:</span>
                    </div>
                    <div className="text-blue-700 mt-1">
                      {serviceForBooking.selectedPracticeName}
                    </div>
                  </div>
                )}

                <PatientInfoForm
                  formData={formData}
                  idValidation={idValidation}
                  idTouched={idTouched}
                  onInputChange={handleInputChange}
                  onIdBlur={() => setIdTouched(true)}
                  readOnly={user?.userType === "patient"}
                />

                {/* Price Display */}
                {serviceRequiresPayment && selectedService && (
                  <div className="bg-purple-50 p-3 rounded-lg flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <CreditCard className="w-4 h-4 text-purple-600" />
                      <span className="text-sm font-medium text-purple-900">
                        Payment required
                      </span>
                    </div>
                    <span className="text-lg font-bold text-purple-900">
                      R{selectedService.base_price?.toFixed(2)}
                    </span>
                  </div>
                )}

                {/* Doctor payment status warning */}
                {serviceRequiresPayment &&
                  formData.doctor_id &&
                  doctorPaymentStatus &&
                  !doctorCanReceivePayments && (
                    <div className="bg-amber-50 p-3 rounded-lg flex items-start gap-2">
                      <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                      <p className="text-sm text-amber-800">
                        This doctor is not set up to receive payments. Please
                        select a different doctor or contact the practice.
                      </p>
                    </div>
                  )}
              </div>

              {/* Middle Column: Calendar */}
              <div className="space-y-4">
                <div>
                  <Label className="text-sm font-medium">Select Date *</Label>
                  {loadingAvailability ? (
                    <div className="mt-2 p-6 text-center bg-slate-50 border border-slate-200 rounded-lg">
                      <Loader2 className="w-8 h-8 mx-auto text-slate-400 mb-3 animate-spin" />
                      <p className="text-slate-600 text-sm">
                        Loading available dates...
                      </p>
                    </div>
                  ) : !loadingAvailability &&
                    availabilityData &&
                    availableDates.size === 0 ? (
                    <div className="mt-2 p-6 text-center bg-amber-50 border border-amber-200 rounded-lg">
                      <CalendarIcon className="w-12 h-12 mx-auto text-amber-400 mb-3" />
                      <h3 className="text-lg font-medium text-amber-800 mb-2">
                        No Available Dates
                      </h3>
                      <p className="text-amber-700 text-sm">
                        There are currently no available appointment slots for
                        this service.
                        {selectedService?.requires_doctor && (
                          <span className="block mt-1">
                            This may be because no doctors are available or
                            assigned to this service.
                          </span>
                        )}
                      </p>
                      <p className="text-amber-600 text-xs mt-2">
                        Please try selecting a different service or contact the
                        practice to schedule.
                      </p>
                    </div>
                  ) : (
                    <>
                      <div className="mt-2 flex justify-center">
                        <Calendar
                      mode="single"
                      selected={selectedDate}
                      onSelect={handleDateSelect}
                      disabled={isDateDisabled}
                      className="rounded-md border"
                      modifiers={{
                        available: (date) =>
                          isDateAvailable(date) &&
                          !isBefore(date, startOfDay(new Date())),
                        unavailable: (date) =>
                          !isDateAvailable(date) &&
                          !isBefore(date, startOfDay(new Date())),
                        today: (date) => isSameDay(date, new Date()),
                        selected: (date) =>
                          selectedDate && isSameDay(date, selectedDate),
                      }}
                      modifiersStyles={{
                        available: {
                          backgroundColor: "#dbeafe",
                          color: "#1d4ed8",
                        },
                        unavailable: {
                          backgroundColor: "#f1f5f9",
                          color: "#94a3b8",
                          textDecoration: "line-through",
                        },
                        today: {
                          border: "2px solid #3b82f6",
                          fontWeight: "bold",
                        },
                        selected: {
                          backgroundColor: "#16a34a",
                          color: "white",
                          fontWeight: "bold",
                          border: "2px solid #15803d",
                          borderRadius: "4px",
                        },
                      }}
                    />
                  </div>

                {/* Legend */}
                <div className="flex justify-center gap-4 text-xs">
                  <div className="flex items-center gap-1">
                    <div className="w-3 h-3 bg-blue-200 rounded"></div>
                    <span>Available</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-3 h-3 bg-green-500 rounded"></div>
                    <span>Selected</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-3 h-3 bg-slate-200 rounded"></div>
                    <span>Unavailable</span>
                  </div>
                </div>
                      </>
                    )}
                </div>
              </div>

              {/* Right Column: Doctor Selection and Time Slots */}
              {availableDates.size > 0 && (
                <div className="space-y-6">
                <DoctorSelection
                  selectedService={selectedService}
                  selectedDate={selectedDate}
                  allSlots={allSlots}
                  doctorFilter={doctorFilter}
                  loadingSlots={actuallyLoadingSlots}
                  practiceId={
                    serviceForBooking?.selectedPracticeId ||
                    serviceForBooking?.practice_id ||
                    user?.practiceId
                  }
                  onDoctorFilterChange={(value) => {
                    setDoctorFilter(value);
                    setFormData((prev) => ({
                      ...prev,
                      start_time: "",
                      end_time: "",
                      doctor_id: value,
                    }));
                  }}
                />

                <TimeSlotSelection
                  selectedDate={selectedDate}
                  selectedService={selectedService}
                  doctorFilter={doctorFilter}
                  formData={formData}
                  availableSlots={availableSlots}
                  loadingSlots={actuallyLoadingSlots}
                  onTimeSlotSelect={handleTimeSlotSelect}
                />

                {/* Selected Time Display */}
                {selectedDate && formData.start_time && (
                  <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                    <div className="text-sm font-medium text-green-900">
                      Selected Time
                    </div>
                    <div className="text-green-700">
                      {format(selectedDate, "EEEE, MMM d, yyyy")} at{" "}
                      {format(
                        new Date(`2024-01-01T${formData.start_time}`),
                        "h:mm a"
                      )}
                      {formData.end_time && (
                        <>
                          {" "}
                          -{" "}
                          {format(
                            new Date(`2024-01-01T${formData.end_time}`),
                            "h:mm a"
                          )}
                        </>
                      )}
                    </div>
                    {formData.doctor_id && (
                      <div className="text-sm text-green-600 mt-1">
                        Doctor:{" "}
                        {allSlots.find(
                          (slot) => slot.doctorId === formData.doctor_id
                        )?.doctorName ||
                          getDoctorName(formData.doctor_id, doctors) ||
                          "Selected Doctor"}
                      </div>
                    )}
                  </div>
                )}
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-6 border-t">
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-gradient-to-r from-blue-600 to-indigo-600"
                disabled={
                  !selectedDate ||
                  !formData.start_time ||
                  createBookingMutation.isPending ||
                  createPaymentMutation.isPending ||
                  (selectedService?.requires_doctor && !formData.doctor_id) ||
                  (serviceRequiresPayment &&
                    formData.doctor_id &&
                    !doctorCanReceivePayments)
                }
              >
                {createBookingMutation.isPending ||
                createPaymentMutation.isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {createPaymentMutation.isPending
                      ? "Processing..."
                      : "Saving..."}
                  </>
                ) : serviceRequiresPayment && formData.doctor_id ? (
                  <>
                    <CreditCard className="w-4 h-4 mr-2" />
                    Continue to Payment
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    {isEditing ? "Update Appointment" : "Schedule Appointment"}
                  </>
                )}
              </Button>
            </div>
          </form>
          )}
        </CardContent>
      </Card>
    </div>
    </>
  );
}
